/**
 * THE EDGE OF THE PLAYFIELD. Owned by `world`.
 *
 * The street grid is finite — `StreetGraph.bounds` is the corridor extents plus 2 m — and
 * until now nothing stopped the truck at that line and nothing marked it. Two separate
 * defects lived there:
 *
 *   1. NO PERIMETER COLLISION. `StaticCollision` clamps its broadphase lookups to `bounds`,
 *      so a query from outside still resolves into the edge cells, but there was no collider
 *      in those cells to hit. The truck drove out of the city and kept going.
 *   2. A LITERAL HOLE IN THE GROUND. `buildOuterGround` tiled the horizon skirt on a 40 m
 *      lattice anchored at the ORIGIN and skipped every cell that overlapped `bounds`.
 *      `bounds` is not lattice-aligned, so the skipped cells overshot the city by up to a
 *      whole cell: 36.9 m of missing ground off the west edge and 34.9 m off the east. Drive
 *      off the outermost sidewalk and the world ended into nothing, exactly as reported.
 *      `outerGroundRing()` re-anchors that skirt to `bounds` itself, so it meets the corridor
 *      with no gap anywhere.
 *
 * The visual treatment is a continuous concrete jersey barrier around all four sides with
 * orange works barricades set against it at intervals — a permanent RUE BARRÉE, which is the
 * most Montreal available way to say "the city stops here".
 *
 * §9 BUDGET. The barrier is ONE merged mesh (one draw call) and emits only the faces that can
 * ever be seen: two inner profile facets, the top coping and a flat back. Six-metre slabs at
 * 8 triangles each — about 2.2k triangles for 1.6 km of barrier. It does not cast, because its
 * shadow would fall on the void beyond the map. The barricades reuse the EXISTING `barricade`
 * PropSet, so they cost zero draw calls and stream by block like every other prop.
 */

/**
 * Ground beyond the block grid, as a ring anchored to `bounds`.
 *
 * Replaces the origin-anchored lattice described in the header. Each of the four rectangles is
 * tiled at approximately `cell` metres with the last step in each direction clipped to the
 * exact boundary, so the four regions tile the whole annulus [-E,E]² minus `bounds` with no
 * seam and no overlap. Measured at +60 quads against the lattice it replaces.
 */
export function outerGroundRing(E, bounds, height, mb, cell = 40) {
  const b = bounds;

  // Split [lo, hi] into ≤`cell` even steps. Always includes both ends.
  const steps = (lo, hi) => {
    const out = [lo];
    const n = Math.max(1, Math.ceil((hi - lo) / cell));
    for (let i = 1; i <= n; i++) out.push(lo + (hi - lo) * (i / n));
    return out;
  };

  const tile = (x0, x1, z0, z1) => {
    if (x1 - x0 < 1e-3 || z1 - z0 < 1e-3) return;
    const xs = steps(x0, x1), zs = steps(z0, z1);
    for (let i = 0; i < xs.length - 1; i++) {
      const a = xs[i], c = xs[i + 1];
      for (let j = 0; j < zs.length - 1; j++) {
        const d = zs[j], e = zs[j + 1];
        mb.quad([a, height(a, d), d], [a, height(a, e), e], [c, height(c, e), e], [c, height(c, d), d]);
      }
    }
  };

  // West and east run the full Z extent; south and north fill only the span between them, so
  // the four rectangles are disjoint and their union is exactly the ring.
  tile(-E, b.minX, -E, E);
  tile(b.maxX, E, -E, E);
  tile(b.minX, b.maxX, -E, b.minZ);
  tile(b.minX, b.maxX, b.maxZ, E);
}

/**
 * Emit the perimeter barrier — geometry, static colliders, and the orange barricades leaning
 * against it.
 *
 * @param {object} T            world TUNING
 * @param {StreetGraph} graph
 * @param {MeshBuilder} mb      merged builder for the concrete (one mesh, one draw call)
 * @param {Array} colliders     the same array `world._build()` feeds to StaticCollision
 * @param {Props} props         for the instanced barricade accents; may be null
 * @param {Rng} rng             seeded fork — N3/N4, never Math.random
 * @returns {{segments:number, barricades:number}}
 */
export function buildBoundary(T, graph, mb, colliders, props, rng) {
  const B = T.boundary;
  const g = graph;

  // The barrier centreline sits `inset` metres INBOARD of the corridor edge, so the slab
  // straddles the outer lip of the outermost sidewalk and reads as standing on the pavement.
  // Its base is on the SIDEWALK PLANE, not y = 0 — see ARCHITECTURE.md §4.9c; the ground here
  // is ~1.4 m up and a hardcoded zero would bury the whole run.
  const west = g.corridorMinX + B.inset;
  const east = g.corridorMaxX - B.inset;
  const south = g.corridorMinZ + B.inset;
  const north = g.corridorMaxZ - B.inset;

  const half = B.baseW * 0.5;
  // Run each wall past the corner so the four close without a notch at the junction.
  const overrun = half + B.baseW;

  let segments = 0, barricades = 0;
  // Terrain runs from about −0.9 m to +1.9 m along the perimeter, so the collider band is
  // derived from the slabs actually emitted rather than guessed as an absolute pair.
  let yLo = Infinity, yHi = -Infinity;

  /** One slab: flared toe, upper face, top coping, flat back. Eight triangles. */
  const slab = (axis, at, a0, a1, side, h) => {
    const mid = (a0 + a1) * 0.5;
    const yBase = (axis === 0 ? g.walkY(at, mid) : g.walkY(mid, at)) - B.embed;
    if (yBase < yLo) yLo = yBase;
    if (yBase > yHi) yHi = yBase;
    const yKnee = yBase + B.kneeH;
    const yTop = yBase + h;

    // Lateral offsets from the wall centreline, `side` pointing at the playfield.
    const uFoot = at + side * half;
    const uKnee = at + side * B.kneeW * 0.5;
    const uTop = at + side * B.topW * 0.5;
    const uBack = at - side * half;

    // Front faces must wind CCW seen from the playfield, so the along-axis order flips with
    // `side` (and again between the two axes, because +x cross +y cross +z does).
    const b0 = side > 0 ? a0 : a1;
    const b1 = side > 0 ? a1 : a0;
    const q = axis === 0
      ? (u0, y0, u1, y1) => mb.quad([u0, y0, b0], [u1, y1, b0], [u1, y1, b1], [u0, y0, b1])
      : (u0, y0, u1, y1) => mb.quad([b1, y0, u0], [b1, y1, u1], [b0, y1, u1], [b0, y0, u0]);

    q(uFoot, yBase, uKnee, yKnee);   // flared toe
    q(uKnee, yKnee, uTop, yTop);     // upper face
    q(uTop, yTop, uBack, yTop);      // coping
    q(uBack, yTop, uBack, yBase);    // flat back
  };

  /** An orange A-frame works barricade set against the inboard face, and a cone beside it. */
  const barricadeAt = (axis, at, along, side) => {
    const yaw = axis === 0 ? Math.PI * 0.5 : 0;
    const place = (out, shift) => {
      const u = at + side * (half + out);
      const a = along + shift;
      return axis === 0 ? [u, a] : [a, u];
    };
    const [bx, bz] = place(B.barricadeOut, 0);
    // props.add() places the instance, its contact blob and its static collider, and picks the
    // owning block for streaming — the same path every city prop takes.
    props.add('barricade', bx, g.walkY(bx, bz), bz, yaw, 1, 1, 1, B.barricadeTint);
    // _cone(), not add(): it is the path that also lays the winter snow cap on the tip. Cones
    // are deliberately not colliders (props.COLLIDE) — you are meant to blast through them.
    const [cx, cz] = place(B.coneOut, B.coneAlong * side);
    props._cone(cx, g.walkY(cx, cz), cz, yaw, false);
  };

  /**
   * @param {0|1} axis  0 = constant X, runs along Z; 1 = constant Z, runs along X
   * @param {number} side  +1 when the playfield is on the +lateral side of the wall
   */
  const wall = (axis, at, from, to, side) => {
    const n = Math.max(1, Math.round((to - from) / B.segment));
    const step = (to - from) / n;
    for (let i = 0; i < n; i++) {
      const a0 = from + step * i;
      // Seeded jitter (N3/N4): each slab was dropped by a crew and has settled a little. The
      // ends stay welded to their neighbours, so a height or lean step is a zero-width seam in
      // the silhouette rather than a hole you can see the void through.
      slab(axis, at + rng.spread(B.leanJitter), a0, a0 + step, side,
        B.height + rng.spread(B.heightJitter));
      segments++;
      if (props && i % B.barricadeEvery === B.barricadePhase) {
        barricadeAt(axis, at, a0 + step * 0.5, side);
        barricades++;
      }
    }
  };

  wall(0, west, south - overrun, north + overrun, +1);
  wall(0, east, south - overrun, north + overrun, -1);
  wall(1, south, west - overrun, east + overrun, +1);
  wall(1, north, west - overrun, east + overrun, -1);

  /**
   * COLLISION — four overlapping half-spaces, not four strips.
   *
   * A strip can be tunnelled, and worse: `StaticCollision._hitBox` resolves a sphere centre
   * that is INSIDE a box along that box's SHALLOWEST axis, which from deep inside a thin wall
   * is the OUTER face. A hard enough hit would eject the truck out of the world instead of
   * pushing it back in. Each slab therefore runs from its inner face out to `FAR`, so from
   * anywhere a truck can reach the shallowest axis is the inner face and every resolution
   * pushes back toward the city.
   *
   * The same argument is why all four run the FULL extent on their other axis and overlap at
   * the corners, rather than each stopping where the next begins. Measured: with the north/
   * south slabs clipped to [wx, ex], a truck at (−260, −260) — outside on both axes at once —
   * sat 1.13 m inside the south slab's own west END FACE, which was then its shallowest axis.
   * It got pushed WEST, deeper out, and oscillated against the west slab forever without ever
   * recovering in Z. Overlapping removes the end faces, and the recovery test now passes from
   * every corner.
   *
   * Tunnelling is impossible for the same reason the ejection was: at the ~120 km/h top speed
   * one fixed step covers 33.3 / 60 = 0.56 m and the slab is kilometres deep.
   *
   * The vertical band is measured off the slabs, not guessed: `yLo`/`yHi` are the real range of
   * the barrier feet, and the clearances put the top far above anything the truck can jump to
   * at the edge (the only ramp in the city is the rue Napoléon crest, which is mid-map). There
   * is therefore no vertical escape either. ARCHITECTURE.md §4.9c applies here too — the ground
   * is nowhere near y = 0, and an absolute pair of numbers would have been a guess.
   *
   * KNOWN CONSEQUENCE: `chasecam._avoidGeometry` raycasts through this same StaticCollision, so
   * backing up to the perimeter pulls the camera in even though the visible barrier is only a
   * metre tall. That is the identical behaviour you already get backing onto any facade in the
   * city, and it is the right trade against a truck that can hop the wall and drive away.
   *
   * The broadphase clamps out-of-range spans into the edge cells, so a slab whose far side
   * lies outside `bounds` is still indexed there and still found by a query from outside.
   */
  const F = 1e5;
  const y0 = yLo - B.colliderDepth, y1 = yHi + B.colliderTop;
  const wx = west + half, ex = east - half;
  const sz = south + half, nz = north - half;
  colliders.push({ minX: -F, maxX: wx, minZ: -F, maxZ: F, y0, top: y1, kind: 'building' });
  colliders.push({ minX: ex, maxX: F, minZ: -F, maxZ: F, y0, top: y1, kind: 'building' });
  colliders.push({ minX: -F, maxX: F, minZ: -F, maxZ: sz, y0, top: y1, kind: 'building' });
  colliders.push({ minX: -F, maxX: F, minZ: nz, maxZ: F, y0, top: y1, kind: 'building' });

  return { segments, barricades };
}
